import { QUOTE_PRODUCT_ID } from '@vertex-protocol/contracts';
import { BigDecimal, BigDecimals } from '@vertex-protocol/utils';
import { KNOWN_PRODUCT_IDS } from '@vertex-protocol/react-client';
import {
  SpotBalanceItem,
  useSpotBalances,
} from 'client/hooks/subaccount/useSpotBalances';
import { MarketFilter } from 'client/types/MarketFilter';
import { clientEnv } from 'common/environment/clientEnv';
import { useMemo } from 'react';

interface BalanceInfo {
  symbol: string;
  amount: BigDecimal;
  valueUsd: BigDecimal;
}

export interface SpotProductBalanceTableItem extends SpotBalanceItem {
  balanceRowKind: 'spot';
  rowId: string;
  balanceInfo: BalanceInfo;
  balanceValueUsd: BigDecimal;
  showBlastNativeYieldInfo: boolean;
}

export interface StableUsdAggregateBalanceTableItem {
  balanceRowKind: 'stable_usd_aggregate';
  rowId: 'stable-usd-aggregate';
  displaySymbol: 'USD';
  balanceValueUsd: BigDecimal;
  sourceProductIds: readonly number[];
}

export type SpotBalanceTableItem =
  | SpotProductBalanceTableItem
  | StableUsdAggregateBalanceTableItem;

interface Params {
  marketFilter?: MarketFilter;
}

const USDC_PRODUCT_ID = 5;
const STABLE_USD_PRODUCT_IDS = new Set([QUOTE_PRODUCT_ID, USDC_PRODUCT_ID]);

function matchesMarketFilter(
  balance: SpotBalanceItem,
  marketFilter?: MarketFilter,
) {
  // Instead of using `useFilteredMarkets`, we do this manually as:
  // 1. market filtering is a superset of the functionality required here (ex. only spot here)
  // 2. market filtering does not include the quote product
  if (
    marketFilter?.productIds &&
    !marketFilter.productIds.includes(balance.productId)
  ) {
    return false;
  }
  if (marketFilter?.amount) {
    // Apparently, BigDecimals.ZERO.isPositive() is true
    switch (marketFilter.amount) {
      case 'nonzero':
        return !balance.amount.isZero();
      case 'positive':
        return balance.amount.isGreaterThan(0);
      case 'negative':
        return balance.amount.isLessThan(0);
    }
  }
  return true;
}

function getShowBlastNativeYieldInfo(productId: number) {
  if (clientEnv.base.brandName !== 'blitz') {
    return false;
  }
  return [
    QUOTE_PRODUCT_ID,
    KNOWN_PRODUCT_IDS.wethBlastSepolia,
    KNOWN_PRODUCT_IDS.wethBlast,
  ].includes(productId);
}

function getSpotProductBalanceTableItem(
  balance: SpotBalanceItem,
): SpotProductBalanceTableItem {
  return {
    ...balance,
    balanceRowKind: 'spot',
    rowId: `spot-${balance.productId}`,
    balanceInfo: {
      amount: balance.amount,
      valueUsd: balance.valueUsd,
      symbol: balance.metadata.token.symbol,
    },
    balanceValueUsd: balance.valueUsd,
    showBlastNativeYieldInfo: getShowBlastNativeYieldInfo(balance.productId),
  };
}

export function buildSpotBalanceTableItems(
  spotBalances: SpotBalanceItem[],
  marketFilter?: MarketFilter,
): SpotBalanceTableItem[] {
  const stableSourceProductIds: number[] = [];
  let stableValueUsd = BigDecimals.ZERO;
  const productRows: SpotProductBalanceTableItem[] = [];

  spotBalances.forEach((balance) => {
    if (!matchesMarketFilter(balance, marketFilter)) {
      return;
    }

    if (
      STABLE_USD_PRODUCT_IDS.has(balance.productId) &&
      balance.amount.isGreaterThanOrEqualTo(0)
    ) {
      stableSourceProductIds.push(balance.productId);
      stableValueUsd = stableValueUsd.plus(balance.valueUsd);
      return;
    }

    productRows.push(getSpotProductBalanceTableItem(balance));
  });

  if (!stableSourceProductIds.length) {
    return productRows;
  }

  return [
    {
      balanceRowKind: 'stable_usd_aggregate',
      rowId: 'stable-usd-aggregate',
      displaySymbol: 'USD',
      balanceValueUsd: stableValueUsd,
      sourceProductIds: stableSourceProductIds,
    },
    ...productRows,
  ];
}

export const useSpotBalancesTable = ({ marketFilter }: Params) => {
  const { balances: spotBalances, ...rest } = useSpotBalances();

  const mappedData: SpotBalanceTableItem[] | undefined = useMemo(() => {
    if (!spotBalances) {
      return;
    }

    return buildSpotBalanceTableItems(spotBalances, marketFilter);
  }, [marketFilter?.amount, marketFilter?.productIds, spotBalances]);

  return {
    balances: mappedData,
    ...rest,
  };
};
